import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ValidatorForm } from "react-material-ui-form-validator";
import { PasswordInput, EmailField } from "../../Components/common";
import { toast } from "react-toastify";
import styled from "@emotion/styled";
import { Box, TextField, CircularProgress, Typography } from "@mui/material";
import LogoIcon from "../../assets/Images/New_logo copy.png";
import { API_URL } from "../../utils/config";
import { SuccessIcon } from "../../assets/icons";
import { CommonButton } from "../../Components/common/commonButton";
import axios from "axios";
import { commonToast } from "../../Components/common";

// Styled components matching login screen
const ForgetPasswordContainer = styled("div")(() => ({
  display: "flex",
  width: "100%",
  height: "100vh",
  overflow: "hidden",
  position: "relative",
  fontFamily: "'Inter', 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
}));

const FormSection = styled("div")(() => ({
  width: "40%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "#ffffff",
  padding: "50px 45px",
  position: "relative",
  boxShadow: "0 0 40px rgba(22, 114, 222, 0.15), -4px 0 20px rgba(0, 0, 0, 0.05)",
  overflow: "hidden",
  backgroundImage: `
    radial-gradient(circle at 2px 2px, rgba(22, 114, 222, 0.03) 1px, transparent 0)
  `,
  backgroundSize: "40px 40px",
  "&::before": {
    content: '""',
    position: "absolute",
    top: "-100px",
    right: "-100px",
    width: "300px",
    height: "300px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, rgba(22, 114, 222, 0.1) 0%, rgba(22, 114, 222, 0.05) 100%)",
    animation: "float 6s ease-in-out infinite",
    zIndex: 0,
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: "-150px",
    left: "-150px",
    width: "400px",
    height: "400px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, rgba(22, 114, 222, 0.08) 0%, rgba(22, 114, 222, 0.03) 100%)",
    animation: "float 8s ease-in-out infinite reverse",
    zIndex: 0,
  },
  "@keyframes float": {
    "0%, 100%": {
      transform: "translateY(0px) rotate(0deg)",
    },
    "50%": {
      transform: "translateY(-20px) rotate(5deg)",
    },
  },
}));

const ImageSection = styled("div")(() => ({
  width: "60%",
  height: "100vh",
  overflow: "visible",
  position: "relative",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  padding: "50px 45px",
  paddingTop: "140px",
  backgroundColor: "#ffffff",
  backgroundImage: `
    radial-gradient(circle at 2px 2px, rgba(22, 114, 222, 0.03) 1px, transparent 0)
  `,
  backgroundSize: "40px 40px",
  "&::before": {
    content: '""',
    position: "absolute",
    top: "-100px",
    right: "-100px",
    width: "300px",
    height: "300px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, rgba(22, 114, 222, 0.1) 0%, rgba(22, 114, 222, 0.05) 100%)",
    animation: "float 6s ease-in-out infinite",
    zIndex: 0,
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: "-150px",
    left: "-150px",
    width: "400px",
    height: "400px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, rgba(22, 114, 222, 0.08) 0%, rgba(22, 114, 222, 0.03) 100%)",
    animation: "float 8s ease-in-out infinite reverse",
    zIndex: 0,
  },
  "@keyframes float": {
    "0%, 100%": {
      transform: "translateY(0px) rotate(0deg)",
    },
    "50%": {
      transform: "translateY(-20px) rotate(5deg)",
    },
  },
  "& img": {
    width: "500px",
    height: "auto",
    maxWidth: "none",
    maxHeight: "none",
    objectFit: "contain",
    objectPosition: "center",
    transition: "transform 0.3s ease",
    position: "relative",
    zIndex: 2,
    filter: "drop-shadow(0 20px 60px rgba(22, 114, 222, 0.3)) drop-shadow(0 0 40px rgba(147, 197, 253, 0.4))",
    "&:hover": {
      transform: "scale(1.05)",
      filter: "drop-shadow(0 25px 70px rgba(22, 114, 222, 0.4)) drop-shadow(0 0 50px rgba(147, 197, 253, 0.5))",
    },
  },
}));

const FormContent = styled("div")(() => ({
  width: "100%",
  maxWidth: "420px",
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  position: "relative",
  zIndex: 2,
  animation: "fadeInUp 0.6s ease-out",
  "@keyframes fadeInUp": {
    "0%": {
      opacity: 0,
      transform: "translateY(20px)",
    },
    "100%": {
      opacity: 1,
      transform: "translateY(0)",
    },
  },
}));

const TitleText = styled("p")(() => ({
  fontSize: "34px",
  fontWeight: "700",
  color: "#1a1a1a",
  margin: "0 0 8px 0",
  fontFamily: "'Inter', 'Poppins', sans-serif",
  letterSpacing: "-0.5px",
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: "-8px",
    left: 0,
    width: "400px",
    height: "4px",
    background: "linear-gradient(90deg, #1672DE 0%, rgba(22, 114, 222, 0.3) 100%)",
    borderRadius: "2px",
  },
}));

const SubtitleText = styled("p")(() => ({
  fontSize: "16px",
  color: "#1672DE",
  margin: "16px 0 0 0",
  fontWeight: "500",
  letterSpacing: "0.2px",
  fontFamily: "'Inter', 'Poppins', sans-serif",
}));

const InfoText = styled("p")(() => ({
  fontSize: "14px",
  color: "#1672DE",
  margin: "8px 0",
  fontWeight: "400",
  fontFamily: "'Inter', 'Poppins', sans-serif",
  lineHeight: "1.6",
}));

// Styled wrapper for input fields
const StyledInputWrapper = styled("div")(() => ({
  width: "100%",
  marginBottom: "16px",
  "& .MuiOutlinedInput-root": {
    fontFamily: "'Inter', 'Poppins', sans-serif",
    fontSize: "15px",
    borderRadius: "10px",
    backgroundColor: "#ffffff !important",
    border: "2px solid #e9ecef",
    transition: "all 0.3s ease",
    "&:hover": {
      borderColor: "#1672DE",
      backgroundColor: "#ffffff !important",
      boxShadow: "0 2px 8px rgba(22, 114, 222, 0.1)",
      "& .MuiOutlinedInput-notchedOutline": {
        border: "none",
      },
      "& .MuiInputBase-input": {
        backgroundColor: "#ffffff !important",
        color: "#1a1a1a !important",
        WebkitTextFillColor: "#1a1a1a !important",
      },
      "& input": {
        backgroundColor: "#ffffff !important",
        color: "#1a1a1a !important",
        WebkitTextFillColor: "#1a1a1a !important",
      },
    },
    "&.Mui-focused": {
      borderColor: "#1672DE",
      backgroundColor: "#ffffff !important",
      boxShadow: "0 4px 12px rgba(22, 114, 222, 0.15)",
      "& .MuiOutlinedInput-notchedOutline": {
        border: "none",
      },
      "& .MuiInputBase-input": {
        backgroundColor: "#ffffff !important",
        color: "#1a1a1a !important",
        WebkitTextFillColor: "#1a1a1a !important",
      },
      "& .MuiOutlinedInput-input": {
        backgroundColor: "#ffffff !important",
        color: "#1a1a1a !important",
        WebkitTextFillColor: "#1a1a1a !important",
      },
      "& input": {
        backgroundColor: "#ffffff !important",
        color: "#1a1a1a !important",
        WebkitTextFillColor: "#1a1a1a !important",
      },
    },
    "& fieldset": {
      border: "none !important",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none !important",
    },
    "& .MuiInputBase-input": {
      backgroundColor: "#ffffff !important",
      color: "#1a1a1a !important",
      WebkitTextFillColor: "#1a1a1a !important",
    },
    "& input": {
      backgroundColor: "#ffffff !important",
      color: "#1a1a1a !important",
      WebkitTextFillColor: "#1a1a1a !important",
    },
  },
  "& .MuiInputBase-input": {
    padding: "12px 14px",
    color: "#1a1a1a !important",
    backgroundColor: "#ffffff !important",
    WebkitTextFillColor: "#1a1a1a !important",
    "&::placeholder": {
      color: "#6c757d",
      opacity: 0.7,
    },
    "&:focus": {
      color: "#1a1a1a !important",
      backgroundColor: "#ffffff !important",
      WebkitTextFillColor: "#1a1a1a !important",
    },
    "&:-webkit-autofill": {
      WebkitBoxShadow: "0 0 0 1000px white inset !important",
      WebkitTextFillColor: "#1a1a1a !important",
      backgroundColor: "#ffffff !important",
      transition: "background-color 5000s ease-in-out 0s",
    },
    "&:-webkit-autofill:hover": {
      WebkitBoxShadow: "0 0 0 1000px white inset !important",
      WebkitTextFillColor: "#1a1a1a !important",
      backgroundColor: "#ffffff !important",
    },
    "&:-webkit-autofill:focus": {
      WebkitBoxShadow: "0 0 0 1000px white inset !important",
      WebkitTextFillColor: "#1a1a1a !important",
      backgroundColor: "#ffffff !important",
    },
  },
  "& .MuiOutlinedInput-input": {
    color: "#1a1a1a !important",
    backgroundColor: "#ffffff !important",
    WebkitTextFillColor: "#1a1a1a !important",
    "&:focus": {
      color: "#1a1a1a !important",
      backgroundColor: "#ffffff !important",
      WebkitTextFillColor: "#1a1a1a !important",
    },
    "&:-webkit-autofill": {
      WebkitBoxShadow: "0 0 0 1000px white inset !important",
      WebkitTextFillColor: "#1a1a1a !important",
      backgroundColor: "#ffffff !important",
      transition: "background-color 5000s ease-in-out 0s",
    },
    "&:-webkit-autofill:hover": {
      WebkitBoxShadow: "0 0 0 1000px white inset !important",
      WebkitTextFillColor: "#1a1a1a !important",
      backgroundColor: "#ffffff !important",
    },
    "&:-webkit-autofill:focus": {
      WebkitBoxShadow: "0 0 0 1000px white inset !important",
      WebkitTextFillColor: "#1a1a1a !important",
      backgroundColor: "#ffffff !important",
    },
  },
  "& input": {
    color: "#1a1a1a !important",
    backgroundColor: "#ffffff !important",
    WebkitTextFillColor: "#1a1a1a !important",
    "&:focus": {
      color: "#1a1a1a !important",
      backgroundColor: "#ffffff !important",
      WebkitTextFillColor: "#1a1a1a !important",
    },
    "&:-webkit-autofill": {
      WebkitBoxShadow: "0 0 0 1000px white inset !important",
      WebkitTextFillColor: "#1a1a1a !important",
      backgroundColor: "#ffffff !important",
      transition: "background-color 5000s ease-in-out 0s",
    },
    "&:-webkit-autofill:hover": {
      WebkitBoxShadow: "0 0 0 1000px white inset !important",
      WebkitTextFillColor: "#1a1a1a !important",
      backgroundColor: "#ffffff !important",
    },
    "&:-webkit-autofill:focus": {
      WebkitBoxShadow: "0 0 0 1000px white inset !important",
      WebkitTextFillColor: "#1a1a1a !important",
      backgroundColor: "#ffffff !important",
    },
  },
}));

const SuccessContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "20px",
  padding: "40px",
  textAlign: "center",
  "& .successIcon": {
    width: "80px",
    height: "80px",
    backgroundColor: "#4caf50",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "10px",
    "& svg": {
      width: "50px",
      height: "50px",
      color: "#ffffff",
    },
  },
  "& p": {
    fontSize: "28px",
    fontWeight: "700",
    color: "#1a1a1a",
    margin: "0",
    fontFamily: "'Inter', 'Poppins', sans-serif",
  },
  "& span": {
    fontSize: "16px",
    color: "#666",
    fontFamily: "'Inter', 'Poppins', sans-serif",
    lineHeight: "1.6",
    "&:last-of-type": {
      fontWeight: "600",
      color: "#1672DE",
    },
  },
}));

const ButtonContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: "12px",
  marginTop: "8px",
  padding: "8px 0",
  flexWrap: "wrap",
}));

// Decorative elements
const DecorativeCircle = styled("div")(({ top, right, left, bottom, size, delay }) => ({
  position: "absolute",
  top: top || "auto",
  right: right || "auto",
  left: left || "auto",
  bottom: bottom || "auto",
  width: size || "120px",
  height: size || "120px",
  borderRadius: "50%",
  background: "linear-gradient(135deg, rgba(22, 114, 222, 0.1) 0%, rgba(22, 114, 222, 0.05) 100%)",
  border: "2px solid rgba(22, 114, 222, 0.1)",
  animation: `pulse 3s ease-in-out infinite ${delay || "0s"}`,
  zIndex: 1,
  "@keyframes pulse": {
    "0%, 100%": {
      transform: "scale(1)",
      opacity: 0.6,
    },
    "50%": {
      transform: "scale(1.1)",
      opacity: 0.8,
    },
  },
}));

const DecorativeLine = styled("div")(({ top, right, left, bottom, width, height, rotation, delay }) => ({
  position: "absolute",
  top: top || "auto",
  right: right || "auto",
  left: left || "auto",
  bottom: bottom || "auto",
  width: width || "200px",
  height: height || "2px",
  background: "linear-gradient(90deg, transparent, rgba(22, 114, 222, 0.3), transparent)",
  transform: `rotate(${rotation || 0}deg)`,
  animation: `slideIn 2s ease-out ${delay || "0s"} both`,
  zIndex: 1,
  "@keyframes slideIn": {
    "0%": {
      opacity: 0,
      transform: `rotate(${rotation || 0}deg) translateX(-50px)`,
    },
    "100%": {
      opacity: 1,
      transform: `rotate(${rotation || 0}deg) translateX(0)`,
    },
  },
}));

const ForgetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = location.state?.type;
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    checkPassword: "",
    otp: "",
  });
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isOTPVerified, setIsOTPVerified] = useState(false);
  const [isResetPasswordSuccess, setIsResetPasswordSuccess] = useState(false);
  const [isApiInProgress, setIsApiInProgress] = useState(false);
  const [emailValidationMessage, setEmailValidationMessage] = useState(null);

  const getEmailVerificationStatus = async (emailId) => {
    try {
      setIsApiInProgress(true);
      const { data } = await axios.post(`${API_URL}users/check-user/`, {
        email: emailId,
      });
      setIsApiInProgress(false);

      const { statusCode } = data;

      if (statusCode === 200) {
        commonToast("success", "OTP sent to the email.");
        setIsEmailVerified(true);
        setEmailValidationMessage("");
      } else {
        commonToast("error", "Email is not Valid !!");
        setIsEmailVerified(false);
      }
    } catch (err) {
      setIsApiInProgress(false);
      if (err.status === 404) {
        commonToast("success", "OTP sent to the email.");
        setIsEmailVerified(true);
      } else {
        setIsEmailVerified(false);
        setEmailValidationMessage(err?.response?.data?.message);
      }
    }
  };

  const getOTPVerificationStatus = async (otpInfo) => {
    try {
      setIsApiInProgress(true);
      const { data } = await axios.post(`${API_URL}users/verify-otp/`, {
        email: formData?.email,
        otp: otpInfo ?? "",
      });
      setIsApiInProgress(false);

      const { statusCode } = data;
      if (statusCode === 200) {
        commonToast("success", "OTP Verified Successfully");
        setIsOTPVerified(true);
      } else {
        commonToast("error", "OTP is not valid !!");
        setIsOTPVerified(false);
      }
    } catch (err) {
      setIsApiInProgress(false);
      setIsOTPVerified(false);
      commonToast("error", "OTP is not valid !!");
    }
  };

  const handleFormSubmit = async () => {
    try {
      setIsApiInProgress(true);
      const { data } = await axios.post(`${API_URL}users/create-password/`, {
        email: formData?.email,
        password: formData?.password,
        repassword: formData?.checkPassword,
      });
      setIsApiInProgress(false);

      const { statusCode } = data;
      if (statusCode === 200) {
        setIsResetPasswordSuccess(true);
      } else {
        setIsResetPasswordSuccess(false);
        commonToast("error", "Unable to update new password!!");
      }
    } catch (err) {
      setIsApiInProgress(false);
      setIsResetPasswordSuccess(false);
      commonToast("error", "Unable to update new password!!");
    }
  };

  const handleChange = (e) => {
    const key = e.target.name;
    const value = e.target.value;
    setFormData({ ...formData, [key]: value });

    // Verify email and send OTP
    if (key === "email") {
      if (value?.includes(".com")) getEmailVerificationStatus(value);
    }

    if (key === "otp") {
      if (value?.length === 6) getOTPVerificationStatus(value);
    }
  };

  ValidatorForm.addValidationRule(
    "isPasswordMatch",
    (value) => {
      return value === formData.password;
    },
    "Passwords do not match"
  );

  const NavigateToLoginPage = () => {
    navigate("/");
  };

  return (
    <ForgetPasswordContainer>
      <FormSection>
        {/* Decorative elements */}
        <DecorativeCircle top="10%" right="10%" size="80px" delay="0s" />
        <DecorativeCircle top="70%" left="5%" size="100px" delay="1s" />
        <DecorativeLine top="20%" right="15%" width="150px" rotation="45deg" delay="0.5s" />
        <DecorativeLine bottom="25%" left="10%" width="180px" rotation="-30deg" delay="1s" />

        {isApiInProgress && (
          <div
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              width: "100%",
              background: "rgba(255, 255, 255, 0.95)",
              zIndex: 1000,
              backdropFilter: "blur(4px)",
            }}
          >
            <CircularProgress
              style={{
                color: "#1672DE",
              }}
            />
          </div>
        )}

        <FormContent>
          {isResetPasswordSuccess ? (
            <SuccessContainer>
              <div className="successIcon">
                <SuccessIcon />
              </div>
              <p>Success</p>
              <span>Your password details are sent to your</span>
              <span>{formData.email}</span>
              <span>Please check the mail and log in.</span>
              <CommonButton
                text="OK"
                handleClick={NavigateToLoginPage}
                style={{
                  width: "100%",
                  backgroundColor: "#1672DE",
                  color: "#ffffff",
                  padding: "12px 14px",
                  fontSize: "16px",
                  fontWeight: "600",
                  textTransform: "none",
                  borderRadius: "10px",
                  boxShadow: "0 4px 16px rgba(22, 114, 222, 0.3)",
                  transition: "all 0.3s ease",
                  fontFamily: "'Inter', 'Poppins', sans-serif",
                  marginTop: "20px",
                  minHeight: "48px",
                }}
                sx={{
                  "&:hover": {
                    backgroundColor: "#1565c0",
                    boxShadow: "0 6px 24px rgba(22, 114, 222, 0.4)",
                    transform: "translateY(-2px)",
                  },
                }}
              />
            </SuccessContainer>
          ) : (
            <>
              <div>
                <TitleText>
                  {navigationType === "update_password" ? "Update Password" : "Forgot Password"}
                </TitleText>
                {navigationType === "update_password" && (
                  <SubtitleText>Please update your password. It is older than 90 days!</SubtitleText>
                )}
              </div>

              <ValidatorForm
                onSubmit={handleFormSubmit}
                style={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%" }}
              >
                <StyledInputWrapper>
                  <EmailField
                    value={formData.email}
                    placeholder="Enter your email"
                    handleChange={handleChange}
                    isDisabled={isEmailVerified}
                    name={"email"}
                    width="100%"
                  />
                </StyledInputWrapper>

                {isEmailVerified && (
                  <>
                    <StyledInputWrapper>
                      <TextField
                        sx={{ width: "100%" }}
                        variant="outlined"
                        size="small"
                        onChange={handleChange}
                        name="otp"
                        value={formData.otp}
                        placeholder="Enter OTP"
                        InputProps={{
                          autoComplete: "new-password",
                        }}
                      />
                    </StyledInputWrapper>
                    <InfoText>
                      If the email is correct and exists in our database, then only you'll receive the OTP by email.
                    </InfoText>

                    {isOTPVerified && (
                      <>
                        <StyledInputWrapper>
                          <PasswordInput
                            value={formData.password}
                            handleChange={handleChange}
                            width="100%"
                            placeholder="Enter new password"
                          />
                        </StyledInputWrapper>
                        <StyledInputWrapper>
                          <PasswordInput
                            name="checkPassword"
                            value={formData.checkPassword}
                            placeholder="Confirm password"
                            handleChange={handleChange}
                            validators={["isPasswordMatch"]}
                            errorMessages={[
                              "Confirmation password does not match with the new password.",
                            ]}
                            width="100%"
                          />
                        </StyledInputWrapper>
                      </>
                    )}
                  </>
                )}

                {emailValidationMessage && (
                  <Typography sx={{ color: "#f44336", fontSize: "14px", fontFamily: "'Inter', 'Poppins', sans-serif" }}>
                    {emailValidationMessage}
                  </Typography>
                )}

                <ButtonContainer>
                  <CommonButton
                    text="Cancel"
                    handleClick={() => navigate("/")}
                    style={{
                      flex: 1,
                      backgroundColor: "#ffffff",
                      color: "#17191B",
                      padding: "12px 14px",
                      fontSize: "16px",
                      fontWeight: "600",
                      textTransform: "none",
                      borderRadius: "10px",
                      border: "2px solid #e9ecef",
                      transition: "all 0.3s ease",
                      fontFamily: "'Inter', 'Poppins', sans-serif",
                      minHeight: "48px",
                    }}
                    sx={{
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                        borderColor: "#1672DE",
                        transform: "translateY(-2px)",
                      },
                    }}
                  />
                  <CommonButton
                    text="Submit"
                    type="submit"
                    disabled={
                      !isEmailVerified ||
                      !isOTPVerified ||
                      !formData.password ||
                      !formData.checkPassword ||
                      formData?.password !== formData?.checkPassword
                    }
                    style={{
                      flex: 1,
                      backgroundColor: "#1672DE",
                      color: "#ffffff",
                      padding: "12px 14px",
                      fontSize: "16px",
                      fontWeight: "600",
                      textTransform: "none",
                      borderRadius: "10px",
                      boxShadow: "0 4px 16px rgba(22, 114, 222, 0.3)",
                      transition: "all 0.3s ease",
                      fontFamily: "'Inter', 'Poppins', sans-serif",
                      minHeight: "48px",
                    }}
                    sx={{
                      "&:hover": {
                        backgroundColor: "#1565c0",
                        boxShadow: "0 6px 24px rgba(22, 114, 222, 0.4)",
                        transform: "translateY(-2px)",
                      },
                      "&:disabled": {
                        backgroundColor: "#e0e0e0",
                        color: "#9e9e9e",
                        boxShadow: "none",
                      },
                    }}
                  />
                </ButtonContainer>
              </ValidatorForm>
            </>
          )}
        </FormContent>
      </FormSection>

      <ImageSection>
        {/* Decorative elements */}
        <DecorativeCircle top="15%" right="8%" size="90px" delay="0.5s" />
        <DecorativeCircle top="75%" left="8%" size="110px" delay="1.5s" />
        <DecorativeLine top="30%" right="12%" width="160px" rotation="35deg" delay="0.8s" />
        <DecorativeLine bottom="30%" left="12%" width="200px" rotation="-25deg" delay="1.2s" />
        <div style={{ position: "relative", display: "inline-block" }}>
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "120%",
              height: "120%",
              background: "radial-gradient(circle, rgba(147, 197, 253, 0.5) 0%, rgba(22, 114, 222, 0.3) 50%, transparent 70%)",
              filter: "blur(50px)",
              zIndex: 1,
            }}
          />
          <img
            src={LogoIcon}
            alt="InsurPremMgmt Logo"
            style={{ position: "relative", zIndex: 2 }}
          />
        </div>
      </ImageSection>
    </ForgetPasswordContainer>
  );
};

export default ForgetPassword;
